import { useEffect, useRef, useState } from 'react';
import type { Choice } from '@tales/story-schema';

interface Props {
  choices: Choice[];
  timer: number | undefined;
  defaultChoiceId: string | undefined;
  onChoose: (id: string, timedOut: boolean) => void;
}

export function ChoiceOverlay({ choices, timer, defaultChoiceId, onChoose }: Props) {
  const [left, setLeft] = useState(timer ?? 0);
  const fired = useRef(false);
  const start = useRef(performance.now());

  useEffect(() => {
    if (!timer) return;
    const total = timer * 1000;
    const id = window.setInterval(() => {
      const remaining = Math.max(0, total - (performance.now() - start.current));
      setLeft(remaining / 1000);
      if (remaining <= 0 && !fired.current) {
        fired.current = true;
        window.clearInterval(id);
        onChoose(defaultChoiceId ?? choices[0]!.id, true);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [timer, defaultChoiceId, choices, onChoose]);

  const pct = timer ? (left / timer) * 100 : 100;
  return (
    <div className="choices" role="group" aria-label="Choose what happens next">
      <div className="prompt">
        <span>What does Mara do?</span>
        {timer ? (
          <div className="timer" style={{ ['--pct' as string]: `${pct}%` }} aria-label={`${Math.ceil(left)} seconds left`}>
            <b>{Math.ceil(left)}</b>
          </div>
        ) : null}
      </div>
      {choices.map((c) => (
        <button
          key={c.id}
          className={`choice${c.id === defaultChoiceId && timer ? ' default' : ''}`}
          style={c.id === defaultChoiceId && timer ? { ['--w' as string]: `${100 - pct}%` } : undefined}
          onClick={() => {
            if (fired.current) return;
            fired.current = true;
            onChoose(c.id, false);
          }}
        >
          <span className="t">{c.text}</span>
          {c.hint ? <span className="h">{c.hint}</span> : null}
        </button>
      ))}
    </div>
  );
}
